import classNames from 'classnames';
import { SearchDropdownResults } from 'components/SearchBar/SearchDropdownResults';
import {
    ComponentProps,
    RefObject,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useRecentSites } from 'components/SearchBar/useRecentSites';
import { NavigationSearchList } from 'types/Navigation';
import { useGet } from 'utils/hooks/useAPI';

const MAX_SEARCH_RESULTS = '10';

export type SearchDropdownHandle = {
    previous: () => void;
    next: () => void;
    enter: () => void;
} & Pick<HTMLDivElement, 'contains'>;

interface SearchDropdownProps extends Omit<ComponentProps<'div'>, 'ref'> {
    open?: boolean;
    ref?: RefObject<SearchDropdownHandle | null>;
    searchQuery?: string;
    onSelected?: () => void;
}
export const SearchDropdown = ({
    role = 'listbox',
    className,
    open = false,
    ref,
    searchQuery = '',
    onSelected = () => {},
    ...props
}: SearchDropdownProps) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const searchParams = new URLSearchParams({
        count: MAX_SEARCH_RESULTS,
        search: searchQuery.trim(),
    });
    const [{ data, loading }] = useGet<NavigationSearchList>(
        `/helios-api/navigation/search?${searchParams}`,
        !searchQuery
    );

    const recentSites = useRecentSites();

    const siteResults = useMemo(() => {
        if (!searchQuery || !data?.results.sites) {
            return [];
        }
        return data.results.sites;
    }, [data?.results, searchQuery]);

    const companyResults = useMemo(() => {
        if (!searchQuery || !data?.results.companies) {
            return [];
        }
        return data.results.companies;
    }, [data?.results, searchQuery]);

    const setSelectedInBounds = useCallback(
        (updatedIndex = 0) => {
            const recentLength = recentSites.length;
            const searchLength = siteResults.length + companyResults.length;
            const upperBound = (searchLength || recentLength) - 1;

            setSelectedIndex(Math.max(0, Math.min(updatedIndex, upperBound)));
        },
        [recentSites, siteResults, companyResults]
    );

    useEffect(() => {
        setSelectedInBounds(0);
    }, [open, setSelectedInBounds]);

    const selectItem = useCallback((index: number) => {
        const links = dropdownRef.current?.getElementsByTagName('a');
        links?.item(index)?.click();
    }, []);

    useImperativeHandle(ref, () => ({
        previous: () => setSelectedInBounds(selectedIndex - 1),
        next: () => setSelectedInBounds(selectedIndex + 1),
        enter: () => selectItem(selectedIndex),
        contains: (node: Node | null) =>
            dropdownRef.current?.contains(node) || false,
    }));

    useEffect(() => {
        const links = dropdownRef.current?.getElementsByTagName('a');
        links?.item(selectedIndex)?.scrollIntoView(true);
    }, [selectedIndex]);

    const companySelectedIndex =
        selectedIndex < siteResults?.length
            ? undefined
            : selectedIndex - siteResults?.length;

    return (
        <div
            ref={dropdownRef}
            role={role}
            aria-expanded={open}
            tabIndex={-1}
            className={classNames(
                'text-primary bg-white border border-gray-200 rounded-lg mt-2 p-2',
                className,
                open ? 'block' : 'hidden'
            )}
            {...props}
        >
            <div className="max-h-96 overflow-auto">
                {searchQuery ? (
                    <>
                        <SearchDropdownResults
                            type="sites"
                            data={siteResults}
                            loading={loading}
                            selectedIndex={selectedIndex}
                            onSelected={onSelected}
                        />
                        <SearchDropdownResults
                            type="companies"
                            data={companyResults}
                            loading={loading}
                            selectedIndex={companySelectedIndex}
                            onSelected={onSelected}
                        />
                    </>
                ) : (
                    <SearchDropdownResults
                        type="recent"
                        data={recentSites}
                        selectedIndex={selectedIndex}
                        onSelected={onSelected}
                    />
                )}
            </div>
        </div>
    );
};

SearchDropdown.displayName = 'SearchDropdown';
